import {
  archivedFacilitiesQuery,
  facilitiesQuery,
} from "@common/api/facilities"
import type { FacilityPublic } from "@common/client"
import { Button } from "@common/components/ui/button"
import { GridTable } from "@common/components/ui/grid-table"
import { useHasPermission } from "@common/hooks/use-permissions"
import { useSuspenseQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
  useParams,
} from "@tanstack/react-router"
import { Building2, Check } from "lucide-react"
import { z } from "zod"
import { FilterBar } from "@/components/layout/filter-bar"
import { ListEmpty } from "@/components/layout/list-empty"
import { ListTabs } from "@/components/layout/list-tabs"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { NameFilter } from "@/components/layout/name-filter"
import {
  SettingsTableCell,
  SettingsTableHead,
  SettingsTableHeader,
  SettingsTableRow,
} from "@/routes/_layout/settings/_panel/-components/settings-table"

const COLUMNS = "grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto]"

const filterSchema = z.object({
  archived: z.boolean().optional().catch(false),
  archived_locations: z.boolean().optional().catch(false),
  search: z.string().optional().catch(""),
})

export const Route = createFileRoute(
  "/_layout/settings/_panel/workspace/facilities/_list",
)({
  validateSearch: (search) => filterSchema.parse(search),
  loaderDeps: ({ search }) => ({ archived: search.archived }),
  loader: ({ context: { queryClient }, deps }) =>
    queryClient.ensureQueryData(
      deps.archived ? archivedFacilitiesQuery() : facilitiesQuery(),
    ),
  component: FacilitiesPage,
})

function FacilitiesPage(): React.ReactElement {
  const { search = "" } = Route.useSearch()

  return (
    <div className="flex h-full flex-col">
      <FilterBar>
        <Toolbar search={search} />
      </FilterBar>
      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1 overflow-y-auto [--filter-bar-height:0px]">
          <FacilityResults search={search} />
        </div>
        <Outlet />
      </div>
    </div>
  )
}

function Toolbar({ search }: { search: string }): React.ReactElement {
  const navigate = useNavigate({ from: Route.fullPath })
  const { archived = false } = Route.useSearch()
  const canCreate = useHasPermission("facility:create")
  const filter = (next: string) =>
    navigate({
      search: (previous) => ({ ...previous, search: next || undefined }),
      replace: true,
    })

  return (
    <ListToolbar
      tabs={
        <ListTabs
          tabs={["active", "archived"] as const}
          tab={archived ? "archived" : "active"}
          label={(tab) => (tab === "active" ? "Active" : "Archived")}
          name="Record status"
          onSelect={(tab) =>
            navigate({
              search: (previous) => ({
                ...previous,
                archived: tab === "archived" || undefined,
              }),
              replace: true,
            })
          }
        />
      }
      actions={
        <>
          <NameFilter search={search} onSearch={filter} />
          {canCreate && (
            <Button asChild size="sm">
              <Link to="/settings/workspace/facilities/create">
                New facility
              </Link>
            </Button>
          )}
        </>
      }
    />
  )
}

function FacilityResults({ search }: { search: string }): React.ReactElement {
  const { archived = false } = Route.useSearch()
  const { data } = useSuspenseQuery(
    archived ? archivedFacilitiesQuery() : facilitiesQuery(),
  )
  const params = useParams({ strict: false })
  const facilityId = "facilityId" in params ? params.facilityId : undefined
  const needle = search.toLowerCase()
  const facilities = data.data.filter((facility) =>
    facility.name.toLowerCase().includes(needle),
  )

  if (facilities.length === 0) {
    return <EmptyState search={search} archived={archived} />
  }

  return (
    <GridTable className={COLUMNS}>
      <SettingsTableHeader>
        <SettingsTableHead className="pl-6">Name</SettingsTableHead>
        <SettingsTableHead>Address</SettingsTableHead>
        <SettingsTableHead className="justify-center pr-6">
          Default
        </SettingsTableHead>
      </SettingsTableHeader>
      {facilities.map((facility) => (
        <FacilityRow
          key={facility.id}
          facility={facility}
          selected={facility.id === facilityId}
        />
      ))}
    </GridTable>
  )
}

function FacilityRow({
  facility,
  selected,
}: {
  facility: FacilityPublic
  selected: boolean
}): React.ReactElement {
  return (
    <SettingsTableRow
      asChild
      archived={Boolean(facility.deleted_at)}
      aria-selected={selected}
      selected={selected}
    >
      <Link
        to="/settings/workspace/facilities/$facilityId"
        params={{ facilityId: facility.id }}
        search={true}
      >
        <SettingsTableCell className="pl-6">
          <span className="truncate">{facility.name}</span>
        </SettingsTableCell>
        <SettingsTableCell className="text-muted-foreground">
          <span className="truncate">{formatAddress(facility)}</span>
        </SettingsTableCell>
        <SettingsTableCell className="justify-center pr-6">
          {facility.is_default && (
            <Check
              className="size-4 text-semantic-success"
              aria-label="Default"
            />
          )}
        </SettingsTableCell>
      </Link>
    </SettingsTableRow>
  )
}

function formatAddress(facility: FacilityPublic): string {
  const locality = [
    facility.address_city,
    facility.address_region,
    facility.address_postal_code,
  ]
    .filter(Boolean)
    .join(" ")

  return [facility.address_line1, facility.address_line2, locality]
    .filter(Boolean)
    .join(", ")
}

function EmptyState({
  search,
  archived,
}: {
  search: string
  archived: boolean
}): React.ReactElement {
  const title = search
    ? "No matching facilities"
    : archived
      ? "No archived facilities"
      : "No facilities"
  const body = search
    ? "Try a different name."
    : archived
      ? "Archived facilities appear here."
      : "Add the locations your team works from."

  return <ListEmpty icon={Building2} title={title} body={body} />
}
